import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { WalkthruItem, WalkthruState } from '@/shared/walkthru';
import { FeatureFlagTypes, MediaSource } from '@/shared/types';
import { TpToastProps } from '@/components/ui/toast';
import type { CsamCredentials } from '@/lib/csam-credentials';

export interface FeaturesContextType {
  /**
   * Live feature-flag snapshots. These are implemented as getters on a
   * *stable* context object (see `main.tsx`), so reading them returns the
   * latest value without the provider object ever changing identity — i.e.
   * consumers are not re-rendered when LaunchDarkly streams a flag update.
   * Prefer the `get*` functions below inside callbacks/effects where you need
   * the freshest value at the moment of use rather than a render snapshot.
   */
  flagsFromLD: { [key: string]: FeatureFlagTypes };
  /** Raw map from simon-says, or null when that client is not connected. */
  flagsFromService: { [key: string]: FeatureFlagTypes } | null;
  featureFlags: { [key: string]: FeatureFlagTypes };
  flagOverrides: { [key: string]: FeatureFlagTypes };
  setFlagOverrides: (
    flagOverrides:
      | { [key: string]: FeatureFlagTypes }
      | ((prev: { [key: string]: FeatureFlagTypes }) => { [key: string]: FeatureFlagTypes }),
  ) => void;
  /** Stable getter — returns the latest merged feature flags at call time. */
  getFeatureFlags: () => { [key: string]: FeatureFlagTypes };
  /** Stable getter — returns the latest raw LaunchDarkly flags at call time. */
  getFlagsFromLD: () => { [key: string]: FeatureFlagTypes };
  /** Stable getter — returns the latest simon-says map at call time. */
  getFlagsFromService: () => { [key: string]: FeatureFlagTypes } | null;
  /** Stable getter — returns the latest local flag overrides at call time. */
  getFlagOverrides: () => { [key: string]: FeatureFlagTypes };
  /**
   * Subscribe to feature-flag changes. Returns an unsubscribe function. Used by
   * {@link useReactiveFeatureFlags} so that `React.memo`'d components can still
   * re-render when flags change even though the context object never changes
   * identity. The listener fires when the merged flags, the raw LaunchDarkly
   * map, or the simon-says map changes. Merged-flag readers should ignore a
   * notification whose snapshot is the same object.
   */
  subscribe: (listener: () => void) => () => void;
}

export const FeaturesContext = createContext<FeaturesContextType | null>(null);

/**
 * Reactively read the latest merged feature flags.
 *
 * Unlike `useContext(FeaturesContext).featureFlags`, this hook subscribes to
 * flag changes, so a component that is wrapped in `React.memo` (and therefore
 * doesn't re-render on unrelated parent renders) still updates when a flag
 * value actually changes — without re-rendering on token refreshes or other
 * unrelated `Main` state updates.
 */
export function useReactiveFeatureFlags(): { [key: string]: FeatureFlagTypes } {
  const ctx = useContext(FeaturesContext);
  const [flags, setFlags] = useState<{ [key: string]: FeatureFlagTypes }>(
    () => ctx?.getFeatureFlags() ?? {},
  );
  useEffect(() => {
    if (!ctx?.subscribe || !ctx?.getFeatureFlags) return;
    // Re-sync in case flags changed between the initial render and this effect
    // attaching its listener.
    setFlags(ctx.getFeatureFlags());
    return ctx.subscribe(() => setFlags(ctx.getFeatureFlags()));
  }, [ctx]);
  return flags;
}

/**
 * Reactively read the simon-says flag map. Null until that client
 * has returned a map. Same subscription as {@link useReactiveFeatureFlags},
 * so a service-only update still reaches this hook. The app applies this map
 * in place of LaunchDarkly only when LaunchDarkly `simonSays` is on and this
 * map's own `simonSays` flag is on.
 */
/**
 * Reactively read the raw LaunchDarkly map. Same subscription as
 * {@link useReactiveFeatureFlags}. Needed while Simon Says is the live
 * source: a LaunchDarkly update then leaves the merged map unchanged, so a
 * component that only subscribes to the merged flags would keep a stale
 * LaunchDarkly column.
 */
export function useReactiveLdFlags(): { [key: string]: FeatureFlagTypes } {
  const ctx = useContext(FeaturesContext);
  const [flags, setFlags] = useState<{ [key: string]: FeatureFlagTypes }>(
    () => ctx?.getFlagsFromLD?.() ?? {},
  );
  useEffect(() => {
    if (!ctx?.subscribe || !ctx?.getFlagsFromLD) return;
    setFlags(ctx.getFlagsFromLD());
    return ctx.subscribe(() => setFlags(ctx.getFlagsFromLD()));
  }, [ctx]);
  return flags;
}

export function useReactiveServiceFlags(): { [key: string]: FeatureFlagTypes } | null {
  const ctx = useContext(FeaturesContext);
  const [flags, setFlags] = useState<{ [key: string]: FeatureFlagTypes } | null>(
    () => ctx?.getFlagsFromService?.() ?? null,
  );
  useEffect(() => {
    if (!ctx?.subscribe || !ctx?.getFlagsFromService) return;
    setFlags(ctx.getFlagsFromService());
    return ctx.subscribe(() => setFlags(ctx.getFlagsFromService()));
  }, [ctx]);
  return flags;
}

/**
 * Reactively read one feature flag. Prefer this over
 * `useReactiveFeatureFlags()` in memoized components that only care about a
 * small subset of flags, otherwise any LaunchDarkly flag update will re-render
 * the component even when the flag it uses did not change.
 */
export function useReactiveFeatureFlagValue(key: string): FeatureFlagTypes | undefined {
  const ctx = useContext(FeaturesContext);
  const keyRef = useRef(key);
  keyRef.current = key;

  const read = () => ctx?.getFeatureFlags()?.[keyRef.current];
  const [value, setValue] = useState<FeatureFlagTypes | undefined>(read);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (!ctx?.subscribe || !ctx?.getFeatureFlags) return;
    const sync = () => {
      const next = ctx.getFeatureFlags()[keyRef.current];
      if (Object.is(valueRef.current, next)) return;
      valueRef.current = next;
      setValue(next);
    };
    sync();
    return ctx.subscribe(sync);
  }, [ctx]);

  return value;
}

export const CsamCredentialsContext = createContext<CsamCredentials>({
  csamLimitedUser: false,
  csamVerifier: false,
});

/** Permission-service CSAM clearance for the signed-in tenant user. */
export function useCsamCredentials(): CsamCredentials {
  return useContext(CsamCredentialsContext);
}

/**
 * Obfuscation toggle state for the content-moderation (CSAM) source currently shown in the
 * app-level fullscreen media viewer. Registered by the source-card MediaDisplay that owns
 * fullscreen so the fullscreen instance can render the Obfuscated/Original switcher and drive
 * the same toggle the inline source card uses.
 */
export interface FullscreenObfuscationState {
  contentModerationType: string;
  showObfuscatedPreview: boolean;
  onToggleObfuscatedPreview: (show: boolean) => void;
}

/** Editing callbacks registered by the owner MediaDisplay when entering fullscreen (e.g. EventModal). */
export interface FullscreenMediaEditingState {
  updateMediaContext: (field: string | object, value?: any) => void;
  /** When true, show Enable Editing / trim controls in the fullscreen player. */
  isEditing: boolean;
  /**
   * Opened from a Displayed Media card to view or set that media's thumbnail.
   * The viewer starts in standard viewing; New Thumbnail enters framing.
   */
  thumbnailEditMode?: boolean;
  /**
   * Horizontal-timeline pop-up only. Closing the maximized view writes the
   * Full Media | Capture switcher onto the event. Absent elsewhere so the
   * view-only handoff stays in place.
   */
  commitViewChoiceOnClose?: boolean;
}

export interface FullscreenTransformStateContextType {
  /** Immediate zoom step from toolbar clicks (drives CSS preview scaling). */
  zoomLevel: number;
  /** Debounced zoom step used for crisp layout / raster sizing. */
  settledZoomLevel: number;
  temporaryRotation: number | undefined;
}

export interface FullscreenTransformActionsContextType {
  setZoomLevel: React.Dispatch<React.SetStateAction<number>>;
  setTemporaryRotation: React.Dispatch<React.SetStateAction<number | undefined>>;
}

export const FullscreenTransformStateContext = createContext<FullscreenTransformStateContextType>({
  zoomLevel: 0,
  settledZoomLevel: 0,
  temporaryRotation: undefined,
});

export const FullscreenTransformActionsContext = createContext<FullscreenTransformActionsContextType>({
  setZoomLevel: () => {},
  setTemporaryRotation: () => {},
});

export interface FullScreenContextType {
  isFullscreen: boolean;
  setIsFullscreen: (isFullscreen: boolean) => void;
  fullscreenMediaItem: MediaSource | null;
  setFullscreenMediaItem: React.Dispatch<React.SetStateAction<MediaSource | null>>;
  linkedMediaItem: MediaSource | null;
  setLinkedMediaItem: (mediaItem: MediaSource | null) => void;
  fullScreenCaptureData: string | undefined;
  setFullScreenCaptureData: (captureData: string | undefined) => void;
  fullscreenObfuscation: FullscreenObfuscationState | null;
  setFullscreenObfuscation: React.Dispatch<React.SetStateAction<FullscreenObfuscationState | null>>;
  fullscreenMediaEditing: FullscreenMediaEditingState | null;
  setFullscreenMediaEditing: React.Dispatch<React.SetStateAction<FullscreenMediaEditingState | null>>;
}

export const FullScreenContext = createContext<FullScreenContextType>({
  isFullscreen: false,
  setIsFullscreen: () => {},
  fullscreenMediaItem: null,
  setFullscreenMediaItem: () => {},
  linkedMediaItem: null,
  setLinkedMediaItem: () => {},
  fullScreenCaptureData: undefined,
  setFullScreenCaptureData: () => {},
  fullscreenObfuscation: null,
  setFullscreenObfuscation: () => {},
  fullscreenMediaEditing: null,
  setFullscreenMediaEditing: () => {},
});

export interface ToastContextType {
  setToast: (toast: TpToastProps | undefined) => void;
  setToastOpen: (open: boolean) => void;
}

export const ToastContext = createContext<ToastContextType>({
  setToast: () => {},
  setToastOpen: () => {},
});

export interface StatsContextType {
  /**
   * Analytics click handler: pass a stable event name, the DOM event, and a `{ userInfo }` slice
   * (often the `StatsContext` value or a local override). Events are sent via Intercom +
   * `/api/protected/intercom`.
   */
  recordStat: (
    eventName: string,
    event: React.MouseEvent<HTMLButtonElement | any>,
    statsContext: {
      userInfo: {
        userId: string;
        sessionId: string;
        caseId: string;
        query: string
      }
    },
  ) => void;

  userInfo: {
    userId: string;
    sessionId: string;
    query: string;
    caseId: string;
    page: string;
  };
}

export const StatsContext = createContext<StatsContextType>({
  recordStat: () => {},
  userInfo: {
    userId: '',
    sessionId: '',
    query: '',
    caseId: '',
    page: ''
  }
});

export interface AuthContextType {
  idToken: string | null;
  /**
   * Legacy field: populated with the **ID token** (not the access token) so
   * existing BFF routes that need tenant claims in the auth cookie keep
   * working. Do NOT use this as a Bearer for direct CORS calls to
   * tranquility services — use `realAccessToken` instead.
   */
  accessToken: string | null;
  /**
   * The real Cognito access token (`token_use: "access"`), sourced from
   * `features.tokens.rawAccessToken` in `main.tsx`. Use this as the
   * Bearer token for direct CORS calls to cell services (e.g.
   * provenance-service). May be `null` before auth initializes.
   */
  realAccessToken: string | null;
  tenantName: string | null;
  orgName: string | null;
  /**
   * Stable getters backed by refs in the provider. The context object itself
   * never changes identity, so consumers are not re-rendered when Cognito
   * refreshes tokens. Use these inside callbacks/effects (e.g. right before a
   * fetch) so you always send the freshest token instead of one captured in a
   * render closure.
   */
  getIdToken: () => string | null;
  getAccessToken: () => string | null;
  getRealAccessToken: () => string | null;
  getTenantName: () => string | null;
  getOrgName: () => string | null;
  /**
   * Subscribe to token changes. Returns an unsubscribe function. Used by
   * {@link useReactiveAuthTokens} so that `React.memo`'d components re-render on
   * a (rare) Cognito token refresh even though the context object never changes
   * identity — without re-rendering on every unrelated parent render.
   */
  subscribe: (listener: () => void) => () => void;
}

const noopUnsubscribe = () => () => {};

export const AuthContext = createContext<AuthContextType>({
  idToken: null,
  accessToken: null,
  realAccessToken: null,
  tenantName: null,
  orgName: null,
  getIdToken: () => null,
  getAccessToken: () => null,
  getRealAccessToken: () => null,
  getTenantName: () => null,
  getOrgName: () => null,
  subscribe: noopUnsubscribe,
});

export interface ReactiveAuthTokens {
  idToken: string | null;
  accessToken: string | null;
  realAccessToken: string | null;
  tenantName: string | null;
  orgName: string | null;
}

/**
 * Reactively read the latest auth tokens. Unlike reading the (stable) context
 * directly, this subscribes to token changes so a `React.memo`'d component
 * re-renders when a token actually refreshes, while still skipping the frequent
 * unrelated parent re-renders that the stable context now filters out.
 */
export function useReactiveAuthTokens(): ReactiveAuthTokens {
  const ctx = useContext(AuthContext);
  const read = (): ReactiveAuthTokens => ({
    idToken: ctx.getIdToken(),
    accessToken: ctx.getAccessToken(),
    realAccessToken: ctx.getRealAccessToken(),
    tenantName: ctx.getTenantName(),
    orgName: ctx.getOrgName(),
  });
  const [tokens, setTokens] = useState<ReactiveAuthTokens>(read);
  useEffect(() => {
    // Re-sync on mount / when the provider changes, then subscribe to refreshes.
    setTokens(read());
    return ctx.subscribe(() => setTokens(read()));
    // `ctx` identity is stable for the app provider; re-running on identity
    // change also covers providers (meta-viewer / immigration) whose value
    // object changes when their tokens change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx]);
  return tokens;
}


/** @deprecated In-app WalkThru is deprecated in favor of Intercom walkthroughs. Stub retained to avoid a large contexts refactor. */
export interface WalkthruContextType {
  walkthrus: Record<string, WalkthruState> | null;
  availableWalkthrus: Record<string, WalkthruItem[]> | null;
  setWalkthru: (name: string, walkthru: WalkthruState) => void;
}

/** @deprecated In-app WalkThru is deprecated in favor of Intercom walkthroughs. Stub retained to avoid a large contexts refactor. */
export const WalkthruContext = createContext<WalkthruContextType>({
  walkthrus: {},
  availableWalkthrus: {} as any,
  setWalkthru: (name: string, walkthru: WalkthruState) => {}
});
